import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { differenceWith } from 'lodash';
import FlyoutContainer from 'components/consumer/FlyoutContainer';
import PDPFullWidthImageAndTextSelector from 'components/consumer/PDP/PDPFullWidthImageAndTextSelector';
import IconInfoNew from 'components/icons/IconInfoNew';
import { colorGrayLight2 } from 'styles/layout/colors';
import {
  PDP_READYTOSHIP_TAB_ID,
  WOOD_STAIN_PRODUCT_OPTION_ID,
  BED_SIZE_PRODUCT_OPTION_ID,
  BB_ORIENTATION_PRODUCT_OPTION_ID,
  FRAME_PRODUCT_OPTION_ID,
  ORIENTATION_PRODUCT_OPTION_ID,
} from 'commons/constants';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import { getCombinedOptionSKUs } from 'data-hooks/useProductLocalStock/utils';
import OrientationPDPModal, {
  orientationOptionIds,
} from '../../OrientationPDPModal';
import PDPImageAndTextOption from './PDPImageAndTextOption';
import usePDPOptions from '../state/context/usePDPOptions';
import usePdpSelectedOptionsVariantSku from '../state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';

const stockWithNonstockExceptions = (stockOptions, optionsWithNonstock) => {
  if (!stockOptions || !optionsWithNonstock) {
    return stockOptions;
  }
  const frameOptionInstock =
    stockOptions.find(option => option.id === FRAME_PRODUCT_OPTION_ID) || null;
  const frameOptionAll =
    optionsWithNonstock.find(option => option.id === FRAME_PRODUCT_OPTION_ID) ||
    null;

  if (frameOptionInstock && frameOptionAll) {
    const nonStockOptionValues = differenceWith(
      frameOptionAll.values,
      frameOptionInstock.values,
      (allValue, instockValue) => allValue.id === instockValue.id
    ).map(option => ({
      ...option,
      isDisabled: true,
    }));
    const stockOptionsWithNonstockExceptions = stockOptions.map(option => {
      if (option.id === FRAME_PRODUCT_OPTION_ID) {
        const optionWithNonstockException = {
          ...option,
          values: [...option.values, ...nonStockOptionValues],
        };
        return optionWithNonstockException;
      }
      return option;
    });

    return stockOptionsWithNonstockExceptions;
  }
  return stockOptions;
};

const getContentForOptionValue = (optionValue, variant) => {
  if (variant === 'regular') {
    return {
      image: optionValue.image,
      text: optionValue.value.trim(),
    };
  }

  if (variant === 'fullWidth') {
    return {
      image: optionValue.image,
      text: optionValue.value.trim(),
      price: `$${optionValue.additional_cost}`,
    };
  }

  return {};
};

const findValuesFromOptions = (
  options,
  optionId,
  variant,
  selectedOptions,
  validStockOptionSkus,
  isRTSTab,
  productOptions
) => {
  if (!options || !optionId) {
    return [];
  }

  const orderedSelectedOption = options.find(
    option => parseInt(option.id, 10) === parseInt(optionId, 10)
  );
  if (!orderedSelectedOption) {
    return [];
  }

  /*
   * The selectedOptions object is received in a format like this: {16: 107, 17: 4561, 19: 757}
   * However, the backend system uses a different order to generate SKUs from options,
   * where the keys are manually reordered to match the backend's logic.
   */
  const orderedSelectedOptions = productOptions?.map(({ id }) => ({
    id,
    value: selectedOptions[id.toString()],
  }));

  const selectedOptionKeys = orderedSelectedOptions.map(option => option.id);
  const optionIdPosition = selectedOptionKeys.indexOf(optionId);

  const otherOptionSkuArray = orderedSelectedOptions
    .filter(option => option.id !== optionId)
    .map(({ id, value }) => {
      const otherOption = options.find(
        option => parseInt(option.id, 10) === parseInt(id, 10)
      );

      return otherOption.values.find(
        otherOptionValue => otherOptionValue.id === value
      ).sku;
    });

  const updatedValues = orderedSelectedOption.values.map(optionValue => {
    const content = getContentForOptionValue(optionValue, variant);
    const combinedSku = [...otherOptionSkuArray];
    combinedSku.splice(optionIdPosition, 0, optionValue.sku);
    const isOutOfStock = !validStockOptionSkus?.includes(combinedSku.join(''));

    return {
      ...optionValue,
      content,
      isSelected:
        selectedOptions?.[orderedSelectedOption.id] === optionValue.id,
      isDisabled: isRTSTab && (isOutOfStock || optionValue?.isDisabled),
    };
  });

  return updatedValues;
};

const PDPImageAndTextOptionDisplay = ({
  hideFlyout = false,
  imageOrTextVariant = 'regular',
  mode = 'pdp',
  optionId = null,
  variant = 'regular',
}) => {
  const { loading, data = {}, actions } = usePDPOptions();

  const [flyOutContent, setFlyOutContent] = useState({});
  const [isFlyOutVisible, setIsFlyOutVisible] = useState(false);
  const containerRef = useRef(null);
  const [showOrientationPDPModal, setShowOrientationPDPModal] = useState(false);

  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();

  const { options = [], selectedOptions = {}, stock = {} } = data;
  const isRTSTab = pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID;

  const finalOptions = isRTSTab
    ? stockWithNonstockExceptions(stock.stockOptions, options)
    : options;

  const pdpSelectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();

  const validStockOptionSkus = useMemo(() => {
    if (!!stock?.stockOptionsCombined?.length && isRTSTab) {
      const updatedDisabledState = getCombinedOptionSKUs(
        pdpSelectedOptionsVariantSku,
        stock?.stockOptionsCombined
      );
      return updatedDisabledState;
    }
    return [];
  }, [stock, pdpSelectedOptionsVariantSku, isRTSTab]);

  const values = findValuesFromOptions(
    finalOptions,
    optionId,
    variant,
    selectedOptions,
    validStockOptionSkus,
    isRTSTab,
    options
  );

  const toggleFlyOut = (isVisible, optionValueId) => {
    if (hideFlyout) {
      return;
    }
    const newTop = containerRef?.current?.getBoundingClientRect().top || 0;
    const selectedValue = values?.find(({ id }) => id === optionValueId);
    if (isVisible && selectedValue) {
      setFlyOutContent({
        top: newTop,
        imageUrl: selectedValue?.image,
        content: selectedValue?.description,
        title: selectedValue?.value.trim(),
      });
    }
    setIsFlyOutVisible(isVisible);
  };

  const onClose = () => {
    setIsFlyOutVisible(false);
  };

  const onOptionClicked = optionValueId => {
    if (typeof actions?.onOptionValueSelection === 'function') {
      actions.onOptionValueSelection(optionId, optionValueId);
    }
  };

  const optionName = options?.find(option => option.id === optionId)?.name;
  const selectedId = selectedOptions?.[optionId];

  return (
    <div
      className={`w-full flex-col flex ${
        optionId !== WOOD_STAIN_PRODUCT_OPTION_ID &&
        optionId !== BED_SIZE_PRODUCT_OPTION_ID &&
        optionId !== BB_ORIENTATION_PRODUCT_OPTION_ID &&
        optionId !== ORIENTATION_PRODUCT_OPTION_ID &&
        optionId !== FRAME_PRODUCT_OPTION_ID &&
        values.length < 2
          ? 'hidden'
          : ''
      }`}
    >
      <h3 className="text-base font-bold text-gray mt-0 mx-0 mb-1.5">
        {`Select ${optionName}`}{' '}
        {orientationOptionIds.includes(optionId) && (
          <button
            type="button"
            onClick={() => {
              setShowOrientationPDPModal(true);
            }}
            className="cursor-pointer align-middle bg-transparent border-0"
          >
            <IconInfoNew color={colorGrayLight2} width={13} height={13} />
          </button>
        )}
      </h3>
      <OrientationPDPModal
        selectedPlan={{}}
        eligiblePlans={[]}
        isOpen={!!showOrientationPDPModal}
        close={() => {
          setShowOrientationPDPModal(false);
        }}
        cartItemId={{}}
        isEdit={{}}
        onOptionClicked={id => {
          onOptionClicked(id);
        }}
        values={values}
        optionId={optionId}
      />
      <div
        className={classnames(
          'w-full flex items-start flex-row flex-nowrap overflow-x-scroll pr-2 lg:flex-wrap lg:pr-0',
          {
            'md:flex-wrap md:pr-0': mode === 'quickview',
            'flex-col': variant === 'fullWidth',
          }
        )}
        ref={containerRef}
      >
        {isFlyOutVisible ? (
          <FlyoutContainer
            top={flyOutContent.top}
            imageUrl={flyOutContent.imageUrl}
            content={flyOutContent.content}
            title={flyOutContent.title}
            isOpen={isFlyOutVisible}
            onClose={onClose}
            flyoutVariant="standard"
          />
        ) : null}
        {loading
          ? [1, 2].map(val => (
              <PDPImageAndTextOption loading={loading} key={val} />
            ))
          : null}
        {!loading && values.length
          ? values.map(optionValue => {
              if (variant === 'fullWidth') {
                return (
                  <PDPFullWidthImageAndTextSelector
                    key={optionValue.id}
                    value={optionValue}
                    onClickAction={onOptionClicked}
                    onHoverAction={toggleFlyOut}
                    optionName={optionName}
                    selectedId={selectedId}
                  />
                );
              }
              return (
                <PDPImageAndTextOption
                  key={optionValue.id}
                  optionValue={optionValue}
                  selectedId={selectedId}
                  onOptionValueSelection={onOptionClicked}
                  variant={imageOrTextVariant}
                  toggleFlyOut={toggleFlyOut}
                />
              );
            })
          : null}
      </div>
    </div>
  );
};

PDPImageAndTextOptionDisplay.propTypes = {
  hideFlyout: PropTypes.bool,
  imageOrTextVariant: PropTypes.oneOf(['regular', 'textOnly']),
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  optionId: PropTypes.number,
  variant: PropTypes.oneOf(['regular', 'fullWidth']),
  values: PropTypes.shape({
    content: PropTypes.arrayOf(
      PropTypes.shape({
        displayValue: PropTypes.string,
        modifier: PropTypes.string,
        type: PropTypes.oneOf(['image', 'text']),
      })
    ),
    isSelected: PropTypes.bool,
  }),
};

export default PDPImageAndTextOptionDisplay;
